"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

import { getOrCreateBootstrapDistinctId } from "@/lib/analytics-identity";
import { sendOwnedAnalyticsEvent } from "@/lib/analytics-server";
import { privacySafeTelemetryPath } from "@/lib/telemetry-route-policy";
import { readMessageLandingParams } from "@/lib/utm-params";

declare global {
  interface Window {
    __ishOwnedPageViewPath?: string;
  }
}

/**
 * Immediate, first-party page-view coverage for Next routes.
 *
 * This intentionally lives outside the deferred analytics gates: it loads no
 * third-party script and writes only to the owned edge beacon. The pathname ref
 * prevents React Strict Mode/effect replays from duplicating a logical route
 * view while still allowing A -> B -> A navigation to record three views.
 */
export default function OwnedPageViewTracker() {
  const pathname = usePathname();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    if (lastTrackedPath.current === pathname) return;
    if (window.__ishOwnedPageViewPath === pathname) {
      lastTrackedPath.current = pathname;
      return;
    }

    const distinctId = getOrCreateBootstrapDistinctId();
    if (!distinctId) return;

    lastTrackedPath.current = pathname;
    window.__ishOwnedPageViewPath = pathname;
    // Attach governed message attribution when the landing carries it (rule 55).
    // Recovery page views include the opaque abandoned order id, making the
    // universal route-entry pulse joinable to sent/paid without depending on a
    // page-specific component. Ordinary page views still carry no extra keys.
    sendOwnedAnalyticsEvent("page_view", {
      distinct_id: distinctId,
      source_path: privacySafeTelemetryPath(pathname),
      traffic_class: "human_candidate",
      ...readMessageLandingParams(),
    });
  }, [pathname]);

  return null;
}
